import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { CheckCircle, X, Loader2 } from 'lucide-react';

interface UsernameSetupProps {
  onComplete?: () => void;
  returnUrl?: string;
}

export const UsernameSetup = ({ onComplete, returnUrl }: UsernameSetupProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { refreshOrganizations } = useOrganization();
  const [username, setUsername] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [availability, setAvailability] = useState<{
    available: boolean;
    checked: boolean;
  }>({ available: false, checked: false });

  const validateUsername = (value: string) => {
    return /^[a-zA-Z0-9]{3,30}$/.test(value);
  };

  const checkAvailability = async (usernameValue: string) => {
    if (!validateUsername(usernameValue)) {
      setAvailability({ available: false, checked: false });
      return;
    }

    setIsChecking(true);
    try {
      const { data, error } = await supabase
        .rpc('check_username_availability', { username_input: usernameValue });

      if (error) throw error;

      setAvailability({ available: data, checked: true });
    } catch (error) {
      console.error('Error checking username availability:', error);
      toast({
        title: t('username.error'),
        description: t('username.failedToCheck'),
        variant: "destructive"
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value.toLowerCase());
    setAvailability({ available: false, checked: false });
    
    if (value.length >= 3) {
      const timeoutId = setTimeout(() => {
        checkAvailability(value.toLowerCase());
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  };

  const handleSave = async () => {
    if (!user || !validateUsername(username)) return;

    console.log('Debug: user object:', user);
    console.log('Debug: user.id:', user.id);
    console.log('Debug: auth.uid() should match user.id');

    setIsSaving(true);
    try {
      // Recheck availability right before saving to ensure it's still available
      console.log('Debug: Rechecking username availability before save');
      const { data: stillAvailable, error: checkError } = await supabase
        .rpc('check_username_availability', { username_input: username });
      
      if (checkError) {
        console.error('Debug: Availability recheck error:', checkError);
        throw checkError;
      }
      
      if (!stillAvailable) {
        console.log('Debug: Username no longer available');
        setAvailability({ available: false, checked: true });
        toast({
          title: t('username.usernameTaken'),
          description: t('username.usernameNoLongerAvailable'),
          variant: "destructive"
        });
        return;
      }
      
      console.log('Debug: Username still available, proceeding with save');
      
      // Get user profile to determine organization type
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_type')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        console.error('Debug: Profile error:', profileError);
        throw profileError;
      }

      // Map user type to organization type
      const orgType = profile.user_type === 'business_customer' ? 'customer' : 'carrier';

      // Check if user has any organizations
      const { data: existingOrgs, error: orgError } = await supabase
        .from('organization_members')
        .select('org_id')
        .eq('user_id', user.id)
        .limit(1);

      if (orgError) throw orgError;

      if (existingOrgs && existingOrgs.length > 0) {
        // Update existing organization's username
        const { error } = await supabase
          .from('organizations')
          .update({ username })
          .eq('id', existingOrgs[0].org_id);

        if (error) throw error;
      } else {
        // Create new organization using secure function with all data from signup
        const companyName = user.user_metadata?.company_name || username;
        const mainBranches = user.user_metadata?.main_branches ? 
          user.user_metadata.main_branches.split(',') : null;
        const shippedItemsDesc = user.user_metadata?.shipped_items_desc || null;
        const preferredVehicles = user.user_metadata?.preferred_vehicles ? 
          user.user_metadata.preferred_vehicles.split(',') : null;
        const averageWeightKg = user.user_metadata?.average_weight_kg ? 
          parseInt(user.user_metadata.average_weight_kg) : null;
        const vehiclesCount = user.user_metadata?.vehicles_count ? 
          parseInt(user.user_metadata.vehicles_count) : null;
        
        console.log('Debug: Creating organization using secure function with data:', {
          user_uuid: user.id,
          org_name: companyName,
          org_username: username,
          org_type: orgType,
          main_branches: mainBranches,
          shipped_items_desc: shippedItemsDesc,
          preferred_vehicles: preferredVehicles,
          average_weight_kg: averageWeightKg,
          vehicles_count: vehiclesCount
        });
        
        const { data: newOrgId, error: createError } = await supabase
          .rpc('create_user_organization', {
            user_uuid: user.id,
            org_name: companyName,
            org_username: username,
            org_type: orgType,
            main_branches: mainBranches,
            shipped_items_desc: shippedItemsDesc,
            preferred_vehicles: preferredVehicles,
            average_weight_kg: averageWeightKg,
            vehicles_count: vehiclesCount
          });

        if (createError) {
          console.error('Debug: Organization creation error:', createError);
          throw createError;
        }
        
        console.log('Debug: Organization created successfully with ID:', newOrgId);
        
        // Refresh organization context to update the Dashboard
        console.log('Debug: Refreshing organization context after creation');
        await refreshOrganizations();
      }

      // Refresh organization context for username updates too
      console.log('Debug: Refreshing organization context after username setup');
      await refreshOrganizations();

      toast({
        title: t('username.success'),
        description: t('username.usernameSet', { username })
      });
      
      // Small delay to ensure organization context is updated
      setTimeout(() => {
        // If there's a returnUrl, redirect there instead of calling onComplete
        if (returnUrl) {
          window.location.href = returnUrl;
        } else {
          onComplete?.();
        }
      }, 100);
    } catch (error) {
      console.error('Error saving username:', error);
      toast({
        title: t('username.error'),
        description: t('username.failedToSave'),
        variant: "destructive"
      });
      // Only set isSaving to false if there was an error
      setIsSaving(false);
    }
    // Don't set isSaving to false on success to keep button disabled during navigation
  };

  const isValid = validateUsername(username);

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-center mb-4">{t('username.chooseUsername')}</CardTitle>
        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('username.usernameExplanation')}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="username" className="ltr:text-left rtl:text-right">{t('username.username')}</Label>
          <div className="relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
              @
            </div>
            <Input
              id="username"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value)}
              placeholder="shohnat"
              className="pl-8 pr-10 direction-ltr"
              maxLength={30}
              dir="ltr"
            />
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {isChecking && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              {!isChecking && availability.checked && isValid && (
                availability.available ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <X className="h-4 w-4 text-red-600" />
                )
              )}
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            {!isValid && username.length > 0 && (
              <p className="text-red-600">{t('username.validation')}</p>
            )}
            {isValid && availability.checked && (
              <p className={availability.available ? "text-green-600" : "text-red-600"}>
                {availability.available ? t('username.available') : t('username.alreadyTaken')}
              </p>
            )}
            {isValid && !availability.checked && username.length >= 3 && (
              <p>{t('username.checking')}</p>
            )}
          </div>
        </div>

        <Button 
          onClick={handleSave}
          disabled={!availability.available || isSaving || !isValid}
          className="w-full bg-gradient-to-r from-accent-green to-accent-blue hover:from-accent-green/90 hover:to-accent-blue/90"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 ltr:mr-2 rtl:ml-2 animate-spin" />
              {t('username.saving')}
            </>
          ) : (
            t('username.setUsername')
          )}
        </Button>
      </CardContent>
    </Card>
  );
};